import React, { useCallback, useEffect, useRef, useState } from 'react'

const MIN_THUMB_HEIGHT = 32
const HIDE_DELAY_MS = 800

interface ThumbMetrics {
    height: number
    top: number
}

interface OverlayScrollbarProps {
    containerRef: React.RefObject<HTMLElement | null>
}

// Scrollbar drawn on top of the scroll container: the native one is hidden (see AnswerArea.scss), so it takes no
// layout space and the column of the messages stays centred without a scrollbar-gutter. It is a zero-size sticky
// anchor in the container; the thumb is positioned inside it from the scroll metrics.
const OverlayScrollbar = ({ containerRef }: OverlayScrollbarProps) => {
    const [thumb, setThumb] = useState<ThumbMetrics | null>(null)
    const [isScrolling, setIsScrolling] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const hideTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

    const update = useCallback(() => {
        const el = containerRef.current
        if (!el) return
        const { clientHeight, scrollHeight, scrollTop } = el
        if (scrollHeight <= clientHeight) {
            setThumb(null)
            return
        }
        const height = Math.max(MIN_THUMB_HEIGHT, (clientHeight * clientHeight) / scrollHeight)
        const top = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - height)
        setThumb({ height, top })
    }, [containerRef])

    useEffect(() => {
        const el = containerRef.current
        if (!el) return

        const handleScroll = () => {
            update()
            setIsScrolling(true)
            clearTimeout(hideTimerRef.current)
            hideTimerRef.current = setTimeout(() => setIsScrolling(false), HIDE_DELAY_MS)
        }

        update()
        el.addEventListener('scroll', handleScroll, { passive: true })

        const observer = new ResizeObserver(() => requestAnimationFrame(update))
        observer.observe(el)
        const content = el.querySelector('.answer-area-wrapper')
        if (content) observer.observe(content)

        return () => {
            el.removeEventListener('scroll', handleScroll)
            observer.disconnect()
            clearTimeout(hideTimerRef.current)
        }
    }, [containerRef, update])

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        const el = containerRef.current
        if (!el || !thumb) return
        event.preventDefault()

        const startY = event.clientY
        const startScrollTop = el.scrollTop
        const scrollableDistance = el.scrollHeight - el.clientHeight
        const thumbTravel = el.clientHeight - thumb.height

        const handleMouseMove = (moveEvent: MouseEvent) => {
            if (thumbTravel <= 0) return
            el.scrollTop = startScrollTop + ((moveEvent.clientY - startY) * scrollableDistance) / thumbTravel
        }
        const handleMouseUp = () => {
            setIsDragging(false)
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }

        setIsDragging(true)
        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)
    }

    const visible = isScrolling || isDragging

    return (
        <div className="answer-area-scrollbar" aria-hidden="true">
            {thumb && (
                <div
                    className={`answer-area-scrollbar__thumb${visible ? ' answer-area-scrollbar__thumb--visible' : ''}`}
                    style={{ height: `${thumb.height}px`, transform: `translateY(${thumb.top}px)` }}
                    onMouseDown={handleMouseDown}
                />
            )}
        </div>
    )
}

export default OverlayScrollbar
